import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import { Store } from '@ngrx/store';
import { Subject, Subscription } from 'rxjs';
import { filter, take, takeUntil, tap } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { LocaleUtils } from 'localeselect/locale';
import { mapUnsupportedLanguage } from '../../../new-account/account.helper';
import { TokenService } from '../../../authentication/token.service';
import { CompanySettings } from '../../../settings/model/company-settings';
import * as fromCompanySettingsStore from '../../../settings/store';
import {
  MerchantAccountNotificationConfig,
  MerchantAccountNotificationElement,
  MerchantAccountNotificationErrorEvent
} from '../../types/merchant-account-notification.types';

@Component({
  selector: 'ls-merchant-account-notification',
  template:
    '<merchant-account-notification #notificationElement></merchant-account-notification>'
})
export class MerchantAccountNotificationComponent implements OnInit, OnDestroy {
  private static readonly ERROR_EVENT_NAME =
    'merchant-account-notification:error';

  @ViewChild('notificationElement')
  private notificationElementRef: ElementRef<
    MerchantAccountNotificationElement
  >;

  private initialized = false;
  private cleanupFn: (() => void) | null = null;
  private ngUnsubscribe: Subject<Subscription> = new Subject();

  constructor(
    private tokenService: TokenService,
    private store: Store<fromCompanySettingsStore.CompanyState>
  ) {}

  ngOnInit(): void {
    if (this.tokenService.hasToken()) {
      this.initialize();
    }

    this.tokenService.tokenChanged$
      .pipe(
        tap((token: string) => {
          if (!!token) {
            this.initialize();
          } else {
            this.cleanup();
          }
        }),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe({
        error: err => console.error('Token subscription error:', err)
      });
  }

  ngOnDestroy(): void {
    this.cleanup();
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  private initialize(): void {
    if (this.initialized) {
      return;
    }

    fromCompanySettingsStore
      .getSettingsFromStore(this.store)
      .pipe(
        takeUntil(this.ngUnsubscribe),
        take(1),
        filter((settings: CompanySettings) => !!settings.masterCompanyId)
      )
      .subscribe((companySettings: CompanySettings) => {
        this.loadScriptAndInit(companySettings);
      });
  }

  private cleanup(): void {
    if (this.cleanupFn) {
      this.cleanupFn();
      this.cleanupFn = null;
    }
    this.initialized = false;
  }

  private loadScriptAndInit(companySettings: CompanySettings): void {
    if (!companySettings.masterCompanyId) {
      console.error(
        'MAS Notification: Invalid masterCompanyId',
        companySettings.masterCompanyId
      );
      return;
    }

    this.loadScript()
      .then(() => {
        this.initElement(companySettings);
      })
      .catch(error => {
        console.error('Failed to load MAS notification script:', error);
      });
  }

  private loadScript(): Promise<void> {
    if (!environment.masUIscriptUrl) {
      return Promise.reject(new Error('masUIscriptUrl is not configured'));
    }

    return new Promise((resolve, reject) => {
      const existingScript = document.querySelector(
        `script[src="${environment.masUIscriptUrl}"]`
      );

      if (!existingScript) {
        const script = document.createElement('script');
        script.type = 'module';
        script.src = environment.masUIscriptUrl;
        script.onerror = () =>
          reject(new Error('Failed to load MAS notification script'));
        document.head.appendChild(script);
      }

      customElements
        .whenDefined('merchant-account-notification')
        .then(() => resolve())
        .catch(reject);
    });
  }

  private initElement(companySettings: CompanySettings): void {
    const element = this.notificationElementRef
      .nativeElement as MerchantAccountNotificationElement;

    this.buildConfig(companySettings).then(config => {
      try {
        element.init(config);
        this.initialized = true;
        this.setupErrorListener(element);
      } catch (error) {
        console.error('Failed to initialize MAS notification:', error);
      }
    });
  }

  private buildConfig(
    companySettings: CompanySettings
  ): Promise<MerchantAccountNotificationConfig> {
    const origin = location.origin;
    const baseOrigin = origin.includes('localhost')
      ? origin
      : origin.replace(/-web\b/, '');

    return (window as any).getLightspeedToken().then(res => {
      return {
        baseUrl: `${baseOrigin}/PosServer/rest/manager/v1/mas-proxy`,
        productCode: 'lsl',
        entityType: 'location',
        entityId: companySettings.masterCompanyId.toString(),
        locale: mapUnsupportedLanguage(LocaleUtils.getLocale()),
        masUiUrl: `${origin}/new-account`,
        customHeaders: {
          ...(environment.masTestMode
            ? {
                'X-mas-test-mode': 'true'
              }
            : {}),
          'X-Auth-Token': res.token
        }
      };
    });
  }

  private setupErrorListener(
    element: MerchantAccountNotificationElement
  ): void {
    if (this.cleanupFn) {
      this.cleanupFn();
    }

    const handleError = (event: Event) => {
      const errorEvent = event as MerchantAccountNotificationErrorEvent;
      console.error('MAS Notification Error:', errorEvent.detail);
    };

    element.addEventListener(
      MerchantAccountNotificationComponent.ERROR_EVENT_NAME,
      handleError
    );

    this.cleanupFn = () => {
      element.removeEventListener(
        MerchantAccountNotificationComponent.ERROR_EVENT_NAME,
        handleError
      );
    };
  }
}
